import { createRoute, Outlet, redirect, useLocation } from "@tanstack/solid-router";
import { rootRoute } from "../__root";
import { api } from "@/lib/api";
import { sessionActor } from "@/lib/session-machine";
import { queryClient } from "@/lib/query-client";
import { appState, PageTransition } from "@fleet/ui";
import { cloneSession, isValidSession, queryKeys } from "@mealplan/shared";
import { readAccountsStateSync } from "@/lib/accounts";
import { resolveActiveAccountView } from "@/lib/active-account";
import { readTabAccountId } from "@/lib/tab-account";
import { SESSION_CHECK_TIMEOUT_MS } from "@/lib/constants";

// MODULE scope on purpose: declared inside `beforeLoad`,
// `unicorn/consistent-function-scoping` flags them as error while `tsc` is green.
const fetchSession = async () =>
  api.GET("/api/auth/get-session", { params: { query: { jwt: true } } });
type FetchResult = Awaited<ReturnType<typeof fetchSession>>;

/**
 * Synchronizes the session machine + cache with the server response. It is also
 * called when the response arrives AFTER the deadline (the request is not
 * canceled), which is what lets `BootManager` redirect afterward.
 */
const syncReply = (reply: FetchResult): FetchResult => {
  const { data: replyData, error: replyError } = reply;
  // This prevents the BootManager from seeing a "pending" or stale session
  // during the route transition, which causes redirection loops.
  sessionActor.send({
    type: "SERVER_REPLY",
    session: isValidSession(replyData) ? cloneSession(replyData) : null,
    error: replyError ?? null,
  });
  queryClient.setQueryData(queryKeys.auth.session(), replyData ?? null);
  return reply;
};

/**
 * Mealplan auth wrapper.
 *
 * NOTE (#13 — de-clone): Mealplan deliberately does NOT use the shared
 * `AuthLayout` (the split brand-panel + glass `AuthCard` shell every clone
 * inherited). Instead each screen owns its own `MealplanAuthScene` — a warm
 * "menu ticket" with a tinted brand header, a perforated rule and the form
 * full-bleed beneath. So this route is a thin wrapper: it only owns the "already
 * authenticated → redirect" gate and the page transition. The background, brand
 * band and scroll container live in `MealplanAuthScene`, rendered per-screen.
 */
function AuthLayoutRoute() {
  const location = useLocation();
  appState.markAsReady();

  return (
    <PageTransition routeKey={location().pathname}>
      <Outlet />
    </PageTransition>
  );
}

export const authRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_auth",
  // If already authenticated, redirect to the app root.
  beforeLoad: async ({ search }) => {
    // Multi-account: `?mode=add` opens /login to ADD another account while the
    // current one stays signed in (Gmail style). The "already authenticated →
    // go home" gate must be skipped here; otherwise the switcher's "Add account"
    // would bounce straight back to the app and a 2nd login could never be added.
    const isAddingAccount = (search as { mode?: string }).mode === "add";
    if (isAddingAccount) {
      return;
    }

    const redirectTarget = (search as { redirect?: string }).redirect;
    const tabAccountId = readTabAccountId();
    if (tabAccountId !== null) {
      const view = resolveActiveAccountView(
        readAccountsStateSync(),
        tabAccountId,
        sessionActor.getSnapshot().context.session,
      );
      // Guest tab → show /login (do NOT redirect). Real account → bounce home.
      if (view.isAuthenticated) {
        // oxlint-disable-next-line @typescript-eslint/only-throw-error
        throw redirect({ to: redirectTarget ?? "/" });
      }
      return;
    }

    // Fast path: check machine state before hitting the network.
    const snapshot = sessionActor.getSnapshot();
    if (!snapshot.matches("priming") && (snapshot.context.session || snapshot.context.isVerified)) {
      if (snapshot.context.session) {
        // oxlint-disable-next-line @typescript-eslint/only-throw-error
        throw redirect({ to: (search as { redirect?: string }).redirect ?? "/" });
      }
      return;
    }

    // Network check — only if machine has no session yet.
    // Use api.GET (not fetchApi) so we can inspect the status without throwing.
    //
    // P23/CC-60 · I1-bis — MANDATORY DEADLINE. Previously this was a BARE `await`,
    // and that `await` blocked route mounting: with a stalled response
    // (not aborted), `/login` stayed at `#root.childElementCount` 0 —
    // measured at 3, 6, 10, 15 and 20 s, in dev AND on the production bundle.
    // Neither a 401 nor a 503 reveals it: both resolve quickly. The sibling
    // `_app` guard ALREADY had this deadline, but as a local literal; that is why `/`
    // rendered and `/login` did not. The shared constant is the fix.
    //
    // The request is NOT canceled: it stays alive and synchronizes machine + cache when
    // it arrives. If a session turns out to exist, `BootManager` takes the user out of the
    // login screen; at most there is a flicker, never a blank screen.
    const inFlight = fetchSession()
      .then(syncReply)
      // Network error — let user try to log in anyway.
      .catch(() => null);

    const timedOut = new Promise<null>((resolve) => {
      setTimeout(() => {
        resolve(null);
      }, SESSION_CHECK_TIMEOUT_MS);
    });

    const result = await Promise.race([inFlight, timedOut]);
    if (!result) {
      // Deadline expired or network error: the login is RENDERED. The late response is
      // already hooked up above and BootManager redirects if it carries a session.
      return;
    }

    // Decide from the machine, not from the raw reply: the machine refuses a reply for a
    // session that LOGOUT revoked, and that refused reply must not bounce a guest home.
    // syncReply has already sent SERVER_REPLY, so the snapshot holds the accepted session.
    // Redirect is thrown OUTSIDE the try/catch so it propagates to TanStack Router.
    if (sessionActor.getSnapshot().context.session) {
      // oxlint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: (search as { redirect?: string }).redirect ?? "/" });
    }
    // 401/403, network error, null or refused session — not authenticated, show login page.
  },
  component: AuthLayoutRoute,
});
